import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { X, Download, Share, PlusSquare, Smartphone, MoreVertical } from "lucide-react";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

export default function PWAInstallBanner() {
  const [deferred, setDeferred] = useState(null);
  const [visible, setVisible] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const [isIos, setIsIos] = useState(false);

  const { data: settings } = useQuery({
    queryKey: ["settings-public"],
    queryFn: async () => (await api.get("/settings/public")).data,
  });
  const appName = settings?.branding?.pwa_short_name || "FantasyRace";

  useEffect(() => {
    const dismissed = localStorage.getItem("pwa-banner-dismissed");
    const standalone =
      window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone;
    if (dismissed || standalone) return;

    const ua = navigator.userAgent.toLowerCase();
    const ios =
      /iphone|ipad|ipod/.test(ua) || (/macintosh/.test(ua) && navigator.maxTouchPoints > 1);
    const android = /android/.test(ua);
    if (!ios && !android) return;
    setIsIos(ios);

    const onPrompt = (e) => {
      e.preventDefault();
      setDeferred(e);
      setVisible(true);
    };
    window.addEventListener("beforeinstallprompt", onPrompt);
    // iOS non ha mai il prompt nativo; su Android il banner compare comunque dopo 3s
    // come fallback se il browser non emette beforeinstallprompt (es. Firefox).
    const fallback = setTimeout(() => setVisible(true), ios ? 0 : 3000);
    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      clearTimeout(fallback);
    };
  }, []);

  if (!visible) return null;

  const dismiss = () => {
    localStorage.setItem("pwa-banner-dismissed", "1");
    setVisible(false);
  };

  const install = async () => {
    if (deferred) {
      deferred.prompt();
      await deferred.userChoice;
      setDeferred(null);
      dismiss();
    } else {
      setHelpOpen(true);
    }
  };

  return (
    <>
      <div
        data-testid="pwa-install-banner"
        className="fixed bottom-[76px] lg:bottom-4 left-3 right-3 md:left-auto md:right-4 md:max-w-sm z-40 rounded-xl border border-[#3F3F4C] bg-[#1A1A1E]/95 backdrop-blur-xl p-4 flex items-start gap-3 shadow-2xl"
      >
        <Smartphone className="w-6 h-6 text-[#FF1E27] shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <p className="font-body-sport font-bold text-white text-xs leading-snug">
            Installa l'App di {appName} sul tuo telefono per un'esperienza più veloce!
          </p>
          <button
            data-testid="pwa-install-button"
            onClick={install}
            className="mt-2.5 flex items-center gap-1.5 rounded-lg bg-[#FF1E27] hover:bg-[#CC1118] transition-colors px-4 py-2 min-h-[44px] font-body-sport font-bold text-[11px] tracking-widest text-white uppercase"
          >
            <Download className="w-3.5 h-3.5" /> Installa Ora
          </button>
        </div>
        <button
          data-testid="pwa-dismiss-button"
          onClick={dismiss}
          className="shrink-0 p-1.5 text-gray-500 hover:text-white transition-colors"
          title="Chiudi"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      <Dialog open={helpOpen} onOpenChange={setHelpOpen}>
        <DialogContent className="bg-[#1A1A1E] border-[#3F3F4C] max-w-sm" data-testid="pwa-install-dialog" aria-describedby={undefined}>
          <DialogHeader>
            <DialogTitle className="font-racing text-white uppercase tracking-wide text-sm">
              {isIos ? "Installa su iPhone / iPad" : "Installa su Android"}
            </DialogTitle>
          </DialogHeader>
          {isIos ? (
            <ol className="space-y-4 text-sm text-gray-300 font-body-sport mt-2">
              <li className="flex items-center gap-3">
                <span className="w-8 h-8 rounded-lg bg-[#242429] flex items-center justify-center shrink-0">
                  <Share className="w-4 h-4 text-[#3B82F6]" />
                </span>
                <span>1. Tocca il pulsante <strong className="text-white">Condividi</strong> nella barra di Safari</span>
              </li>
              <li className="flex items-center gap-3">
                <span className="w-8 h-8 rounded-lg bg-[#242429] flex items-center justify-center shrink-0">
                  <PlusSquare className="w-4 h-4 text-[#3B82F6]" />
                </span>
                <span>2. Scorri e tocca <strong className="text-white">"Aggiungi alla schermata Home"</strong></span>
              </li>
              <li className="flex items-center gap-3">
                <span className="w-8 h-8 rounded-lg bg-[#242429] flex items-center justify-center shrink-0 font-racing text-[#FF1E27] text-xs font-black">
                  3
                </span>
                <span>Conferma con <strong className="text-white">"Aggiungi"</strong> — l'icona apparirà sulla Home!</span>
              </li>
            </ol>
          ) : (
            <ol className="space-y-4 text-sm text-gray-300 font-body-sport mt-2">
              <li className="flex items-center gap-3">
                <span className="w-8 h-8 rounded-lg bg-[#242429] flex items-center justify-center shrink-0">
                  <MoreVertical className="w-4 h-4 text-[#3B82F6]" />
                </span>
                <span>1. Tocca il <strong className="text-white">menu ⋮</strong> in alto a destra nel browser</span>
              </li>
              <li className="flex items-center gap-3">
                <span className="w-8 h-8 rounded-lg bg-[#242429] flex items-center justify-center shrink-0">
                  <PlusSquare className="w-4 h-4 text-[#3B82F6]" />
                </span>
                <span>2. Scegli <strong className="text-white">"Installa app"</strong> o <strong className="text-white">"Aggiungi a schermata Home"</strong></span>
              </li>
              <li className="flex items-center gap-3">
                <span className="w-8 h-8 rounded-lg bg-[#242429] flex items-center justify-center shrink-0 font-racing text-[#FF1E27] text-xs font-black">
                  3
                </span>
                <span>Conferma con <strong className="text-white">"Installa"</strong> — l'icona apparirà sulla Home!</span>
              </li>
            </ol>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
